import React, { useMemo } from 'react';
import { getUnitById, getLessonsByUnitId } from '../data/learningPathsData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const UnitDetailPage = ({ unitId, language = 'traditional', onBack, onLessonClick }) => {
  const unitLocal = getUnitById(unitId);
  const lessonsLocal = getLessonsByUnitId(unitId);

  const { data: remote } = useSupabaseData({
    table: 'eng_lessons',
    mapRow: (row) => row,
    getFallback: () => null,
    query: {
      // eq: { unit_id: unitId }
    }
  });

  const { unit, lessons } = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) {
      return { unit: unitLocal, lessons: lessonsLocal };
    }
    const rows = Array.isArray(remote) ? remote : [remote];
    const outLessons = rows
      .filter(r => (r.type || r.section) === 'lesson' || r.lesson_id || r.unit_id)
      .map((r, idx) => ({
        id: r.lesson_id || r.id,
        lessonNumber: r.lesson_number || r.order || idx + 1,
        type: r.type_value || r.type || 'grammar',
        typeIcon: r.type_icon || 'G',
        title: { traditional: r.title_tc || r.title, simplified: r.title_sc || r.title, english: r.title_en || r.title },
        description: { traditional: r.description_tc || r.description, simplified: r.description_sc || r.description, english: r.description_en || r.description },
        isUnlocked: r.is_unlocked !== false,
        isCompleted: Boolean(r.is_completed)
      }));
    const outUnit = unitLocal || (rows.find(r => (r.type || r.section) === 'unit') ? {
      id: unitId,
      unitNumber: rows[0]?.unit_number || 1,
      title: { traditional: rows[0]?.unit_title_tc || '單元', simplified: rows[0]?.unit_title_sc || '单元', english: rows[0]?.unit_title_en || 'Unit' },
      description: { traditional: rows[0]?.unit_desc_tc || '', simplified: rows[0]?.unit_desc_sc || '', english: rows[0]?.unit_desc_en || '' },
      estimatedDuration: rows[0]?.estimated_duration || '15 min',
      difficulty: rows[0]?.difficulty || 'beginner'
    } : null);
    return { unit: outUnit, lessons: outLessons.length > 0 ? outLessons : lessonsLocal };
  }, [remote, unitId]);

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  const getLessonTypeIcon = (type, typeIcon) => {
    switch (type) {
      case 'grammar':
        return (
          <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center text-white text-sm font-bold">
            {typeIcon}
          </div>
        );
      case 'pronunciation':
        return (
          <div className="w-8 h-8 bg-green-500 rounded flex items-center justify-center text-white text-lg">
            🎤
          </div>
        );
      case 'reading':
        return (
          <div className="w-8 h-8 bg-purple-500 rounded flex items-center justify-center text-white text-lg">
            📖
          </div>
        );
      case 'vocabulary':
        return (
          <div className="w-8 h-8 bg-orange-500 rounded flex items-center justify-center text-white text-lg">
            📚
          </div>
        );
      case 'speaking':
        return (
          <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center text-white text-lg">
            🗣️
          </div>
        );
      case 'test':
        return (
          <div className="w-8 h-8 bg-gray-500 rounded flex items-center justify-center text-white text-lg">
            📝
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center text-white text-sm">
            •
          </div>
        );
    }
  };

  const getLessonStatusIcon = (lesson) => {
    if (lesson.isCompleted) {
      return <div className="text-green-500 text-xl">✓</div>;
    } else if (lesson.isUnlocked) {
      return <div className="w-6 h-6 bg-green-500 rounded-full"></div>;
    } else {
      return null;
    }
  };

  if (!unit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <div className="px-6 py-8">
          <button onClick={onBack} className="text-blue-400 mb-4">
            ← 返回
          </button>
          <div className="text-center py-8">
            <p>單元未找到</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="text-white">
            ←
          </button>
          <div className="text-center flex-1">
            <h1 className="text-xl font-bold">
              {getText(unit.title)}
            </h1>
            <p className="text-sm text-gray-300 mt-1">
              Unit {unit.unitNumber}
            </p>
          </div>
          <div className="w-8"></div> {/* Spacer for centering */}
        </div>

        {/* Unit Info */}
        <div className="bg-gray-800/30 border border-gray-600 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {unit.unitNumber}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold mb-2">
                {getText(unit.title)}
              </h2>
              <p className="text-gray-300 text-sm mb-3">
                {getText(unit.description)}
              </p>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>{unit.estimatedDuration}</span>
                <span className={`px-2 py-1 rounded ${
                  unit.difficulty === 'beginner' ? 'bg-green-500/20 text-green-300' :
                  unit.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-300' :
                  'bg-red-500/20 text-red-300'
                }`}>
                  {unit.difficulty.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Lessons List */}
        <div className="space-y-3">
          {lessons.map((lesson) => (
            <div
              key={lesson.id}
              className={`bg-gray-800/30 border border-gray-600 rounded-xl p-4 ${
                lesson.isUnlocked ? 'cursor-pointer hover:bg-gray-700/50' : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={() => lesson.isUnlocked && onLessonClick(lesson.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Lesson Icon */}
                  {getLessonTypeIcon(lesson.type, lesson.typeIcon)}
                  
                  {/* Lesson Info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm text-gray-400">
                        Lesson {lesson.lessonNumber}
                      </span>
                      <span className="text-sm text-gray-500">•</span>
                      <span className="text-sm text-gray-400 capitalize">
                        {lesson.type}
                      </span>
                    </div>
                    <h3 className="font-semibold text-white mb-1">
                      {getText(lesson.title)}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {getText(lesson.description)}
                    </p>
                  </div>
                </div>
                
                {/* Status Icon */}
                <div className="flex-shrink-0">
                  {getLessonStatusIcon(lesson)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UnitDetailPage;
